import { Commitment, ConnectionConfig } from "@solana/web3.js";
import React, { useMemo } from "react";
import { ConnectionProvider, WalletProvider } from "@solana/wallet-adapter-react";
import { SolanaMobileWalletAdapter, createDefaultAddressSelector } from "@solana-mobile/wallet-adapter-mobile";
import { Adapter, BaseWalletAdapter } from "@solana/wallet-adapter-base";
import { authorizationResultCache } from "@axvdex/utils/solanaWallet";
import { useAppDispatch } from "@axvdex/state";
import { connectSolanaWalletWithDispatch } from "@axvdex/state/wallet/solana/walletThunks";
import { UserBalanceProvider } from "./useUserBalance";

interface SolanaContextProviderProps {
  children: React.ReactNode;
}

const SolanaContextProvider: React.FC<SolanaContextProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();

  // Setup the connecton to Solana network
  const solana_rpc_endpoint = process.env.REACT_APP_SOLANA_NETWORK;
  const solana_rpc_config: Commitment | ConnectionConfig = {
    commitment: "confirmed",
    wsEndpoint: process.env.REACT_APP_SOLANA_NETWORK_WS,
  };
  let chain = "solana:mainnet";
  if (process.env.REACT_APP_SOLANA_NETWORK.includes("devnet")) {
    chain = "solana:devnet";
  } else if (process.env.REACT_APP_SOLANA_NETWORK.includes("testnet")) {
    chain = "solana:testnet";
  }

  // Add the mobile wallet adapter to the list of wallets
  const wallets = useMemo(
    () => [
      new SolanaMobileWalletAdapter({
        addressSelector: createDefaultAddressSelector(),
        appIdentity: {
          name: "Astrovault",
          uri: "https://astrovault.io",
          icon: "assets/logos/astrovault.svg",
        },
        authorizationResultCache: authorizationResultCache,
        chain: chain as `${string}:${string}`,
        onWalletNotFound: async () => {
          console.log("No wallet found, please download a wallet app.");
          // Do nothing since the connect button is displaying "download" on the page
        },
      }),
    ],
    []
  );

  // Auto-connect the wallet if it is available
  const handleWalletConnectDispatch = async (walletAdapter: Adapter) => {
    try {
      console.log("auto-connecting wallet | adapter: ", walletAdapter);
      if (walletAdapter instanceof BaseWalletAdapter) {
        await dispatch(connectSolanaWalletWithDispatch({ walletAdapter }));
        return true;
      }
    } catch (error) {
      console.error("Failed to auto-connect", error);
    }
    return false;
  };

  const errorHandler = (error: unknown, adapter?: Adapter) => {
    console.debug(`[Error] [from ${adapter?.name ?? ""} wallet]`, error);
  };

  return (
    <ConnectionProvider endpoint={solana_rpc_endpoint} config={solana_rpc_config}>
      <UserBalanceProvider>
        <WalletProvider
          wallets={wallets}
          localStorageKey="AstrovaultSolanaWalletName"
          autoConnect={handleWalletConnectDispatch}
          onError={errorHandler}
        >
          {children}
        </WalletProvider>
      </UserBalanceProvider>
    </ConnectionProvider>
  );
};

export default SolanaContextProvider;
